import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Container as ContainerIcon, Loader2, MapPin, Ship, ShoppingBag, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Container } from '@/components/site/Section';
import { QuantityStepper, cartLines } from '@/components/site/CartSheet';
import { CustomerFields, DatePicker, EMPTY_CUSTOMER, TextField, validateCustomer } from '@/components/site/forms';
import { useCart } from '@/state/cart';
import { useCatalog } from '@/state/catalog';
import { apiPost, formatKr, formatNumber } from '@/lib/api';
import { cn } from '@/lib/utils';

const ICONS = { hent: MapPin, tippbil: Truck, kranbil: ContainerIcon, baat: Ship };

function Step({ n, title, children }) {
  return (
    <section className="hr-surface p-6 sm:p-8" aria-labelledby={`step-${n}`}>
      <div className="flex items-center gap-3">
        <span className="hr-tabular flex h-7 w-7 items-center justify-center rounded-full bg-[var(--hr-navy)] text-[12.5px] font-semibold text-white" aria-hidden="true">
          {n}
        </span>
        <h2 id={`step-${n}`} className="font-display text-[19px] font-semibold text-[var(--hr-heading)]">
          {title}
        </h2>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

export default function CheckoutPage() {
  const cart = useCart();
  const catalog = useCatalog();
  const navigate = useNavigate();
  const lines = useMemo(() => cartLines(cart.items, catalog.bySlug), [cart.items, catalog.bySlug]);
  const total = lines.reduce((s, l) => s + (l.lineTotal ?? 0), 0);
  const tonnes = lines.reduce((s, l) => s + (Number(l.quantityTonnes) || 0), 0);

  const [delivery, setDelivery] = useState({ method: 'tippbil', address: '', postalCode: '', city: '', requestedDate: null, note: '' });
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    document.title = 'Bestilling · Hardanger Rock';
  }, []);

  const needsAddress = delivery.method !== 'hent';

  const validate = () => {
    const e = { customer: validateCustomer(customer) };
    if (needsAddress) {
      if (!delivery.address.trim()) e.address = 'Skriv inn leveringsadresse.';
      if (!delivery.city.trim()) e.city = 'Skriv inn poststed.';
    }
    if (lines.some((l) => !(Number(l.quantityTonnes) > 0))) e.items = 'Alle linjer må ha en mengde større enn 0.';
    const hasErrors = Object.keys(e.customer).length > 0 || e.address || e.city || e.items;
    setErrors(e);
    return !hasErrors;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError(null);
    if (!lines.length) return;
    if (!validate()) {
      toast.message('Noen felt mangler', { description: 'Se over feltene markert i rødt.' });
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        items: lines.map((l) => ({ slug: l.slug, quantityTonnes: Number(l.quantityTonnes) })),
        delivery: {
          method: delivery.method,
          address: needsAddress ? delivery.address.trim() : null,
          postalCode: needsAddress ? delivery.postalCode.trim() || null : null,
          city: needsAddress ? delivery.city.trim() : null,
          requestedDate: delivery.requestedDate,
          note: delivery.note.trim() || null,
        },
        customer: { name: customer.name.trim(), company: customer.company.trim() || null, email: customer.email.trim(), phone: customer.phone.trim(), orgNumber: customer.orgNumber.replace(/\s/g, '') || null },
        message: message.trim() || null,
      };
      const order = await apiPost('/orders', body);
      cart.clear();
      navigate(`/bestill/bekreftet/${order.id}`, { replace: true, state: { order } });
    } catch (e) {
      setServerError(e.message || 'Noe gikk galt. Prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (catalog.status === 'ready' && lines.length === 0) {
    return (
      <Container className="py-24">
        <div className="mx-auto flex max-w-[480px] flex-col items-center text-center" data-testid="checkout-empty">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--hr-inset)] text-[var(--hr-muted)]">
            <ShoppingBag className="h-6 w-6" aria-hidden="true" />
          </span>
          <h1 className="mt-5 font-display text-[28px] font-semibold text-[var(--hr-heading)]">Bestillingen er tom</h1>
          <p className="mt-2 text-[15px] text-[var(--hr-muted)]">Legg til produkter og mengde før du går videre til bestilling.</p>
          <Button asChild className="mt-6 h-11 rounded-[10px] bg-[var(--hr-navy)] px-5 text-white hover:bg-[var(--hr-blue)]">
            <Link to="/produkter" data-testid="checkout-empty-products-link">
              Se produkter <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className="bg-[var(--hr-bg)]" data-testid="checkout-page">
      <Container className="py-10 sm:py-14">
        <nav aria-label="Brødsmuler" className="text-[13px] text-[var(--hr-muted)]">
          <Link to="/" className="hover:text-[var(--hr-heading)]">
            Forsiden
          </Link>
          <span className="mx-2 text-[var(--hr-muted-2)]">/</span>
          <span className="text-[var(--hr-text)]">Bestilling</span>
        </nav>
        <h1 className="mt-3 font-display text-[32px] font-semibold tracking-[-0.02em] text-[var(--hr-heading)] sm:text-[40px]" data-testid="checkout-title">
          Fullfør bestillingen
        </h1>
        <p className="mt-2 max-w-[60ch] text-[15.5px] leading-7 text-[var(--hr-muted)]">Velg levering og legg inn kontaktinformasjon. Vi bekrefter dato, frakt og endelig pris på e-post eller telefon. Ingen betaling nå.</p>

        <form onSubmit={submit} noValidate className="mt-10 grid items-start gap-8 lg:grid-cols-[1fr_400px]" data-testid="checkout-form">
          <div className="space-y-6">
            <Step n={1} title="Levering">
              <RadioGroup value={delivery.method} onValueChange={(v) => setDelivery((d) => ({ ...d, method: v }))} className="grid gap-3 sm:grid-cols-2" aria-label="Leveringsmåte">
                {catalog.deliveryMethods.map((m) => {
                  const Icon = ICONS[m.id] ?? Truck;
                  const active = delivery.method === m.id;
                  return (
                    <Label key={m.id} htmlFor={`delivery-${m.id}`} className={cn('flex cursor-pointer items-start gap-3 rounded-[12px] border p-4 transition-colors duration-150', active ? 'border-[var(--hr-navy)] bg-[var(--hr-blue-bg)]' : 'border-[var(--hr-border)] bg-white hover:border-[rgba(31,33,48,0.28)]')} data-testid={`delivery-option-${m.id}`}>
                      <RadioGroupItem id={`delivery-${m.id}`} value={m.id} className="mt-0.5 border-[var(--hr-border)] text-[var(--hr-navy)]" />
                      <span className="flex-1">
                        <span className="flex items-center gap-2 text-[15px] font-semibold text-[var(--hr-heading)]">
                          <Icon className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> {m.label}
                        </span>
                        <span className="mt-1 block text-[13px] font-normal leading-5 text-[var(--hr-muted)]">{m.description}</span>
                      </span>
                    </Label>
                  );
                })}
              </RadioGroup>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                {needsAddress && (
                  <>
                    <TextField id="delivery-address" label="Leveringsadresse" value={delivery.address} onChange={(v) => setDelivery((d) => ({ ...d, address: v }))} error={errors.address} autoComplete="street-address" placeholder="Gate/vei og nummer, eller stedsbeskrivelse" className="sm:col-span-2" />
                    <TextField id="delivery-postalCode" label="Postnummer" optional value={delivery.postalCode} onChange={(v) => setDelivery((d) => ({ ...d, postalCode: v }))} inputMode="numeric" placeholder="5464" />
                    <TextField id="delivery-city" label="Poststed" value={delivery.city} onChange={(v) => setDelivery((d) => ({ ...d, city: v }))} error={errors.city} autoComplete="address-level2" placeholder="Dimmelsvik" />
                  </>
                )}
                <DatePicker id="delivery-date" label={needsAddress ? 'Ønsket leveringsdato' : 'Ønsket hentedato'} optional value={delivery.requestedDate} onChange={(v) => setDelivery((d) => ({ ...d, requestedDate: v }))} hint="Vi bekrefter dato ved oppfølging." />
                <TextField id="delivery-note" label="Merknad til levering" optional value={delivery.note} onChange={(v) => setDelivery((d) => ({ ...d, note: v }))} placeholder="F.eks. adkomst, tippested, kontaktperson på plassen" />
              </div>
            </Step>

            <Step n={2} title="Kontaktinformasjon">
              <CustomerFields value={customer} onChange={setCustomer} errors={errors.customer ?? {}} />
              <div className="mt-5">
                <TextField id="order-message" label="Melding til oss" optional multiline value={message} onChange={setMessage} placeholder="Er det noe vi bør vite om prosjektet eller leveransen?" />
              </div>
            </Step>
          </div>

          <aside className="hr-surface lg:sticky lg:top-24" data-testid="order-summary">
            <div className="border-b border-[var(--hr-hairline)] px-6 py-5">
              <h2 className="font-display text-[18px] font-semibold text-[var(--hr-heading)]">Oppsummering</h2>
              <p className="mt-0.5 text-[13px] text-[var(--hr-muted)]">{formatNumber(tonnes, 1)} tonn · {lines.length} {lines.length === 1 ? 'produkt' : 'produkter'}</p>
            </div>
            <ul className="divide-y divide-[var(--hr-hairline)] px-6">
              {lines.map((l) => (
                <li key={l.slug} className="py-4" data-testid={`summary-line-${l.slug}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-[14.5px] font-semibold text-[var(--hr-heading)]">{l.product.name}</p>
                      <p className="hr-tabular text-[12.5px] text-[var(--hr-muted)]">{formatNumber(l.product.pricePerTonne)} kr/tonn</p>
                    </div>
                    <button type="button" onClick={() => cart.remove(l.slug)} aria-label={`Fjern ${l.product.name}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[var(--hr-muted)] hover:bg-[var(--hr-red-bg)] hover:text-[var(--hr-red)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`summary-remove-${l.slug}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="mt-2.5 flex items-center justify-between gap-3">
                    <QuantityStepper value={l.quantityTonnes} onChange={(v) => cart.setQuantity(l.slug, v)} testId={`summary-qty-${l.slug}`} />
                    <span className="hr-tabular text-[14.5px] font-semibold text-[var(--hr-heading)]">{l.lineTotal !== null ? formatKr(l.lineTotal) : '–'}</span>
                  </div>
                </li>
              ))}
            </ul>
            {errors.items && (
              <p className="px-6 pb-2 text-[12.5px] text-[var(--hr-red)]" role="alert">
                {errors.items}
              </p>
            )}
            <div className="border-t border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-6 py-5">
              <div className="flex items-baseline justify-between">
                <span className="text-[14.5px] font-medium text-[var(--hr-heading)]">Estimert sum eks. mva</span>
                <span className="hr-tabular font-display text-[26px] font-semibold text-[var(--hr-heading)]" data-testid="summary-total">{formatKr(total)}</span>
              </div>
              <p className="mt-1 text-[12.5px] leading-5 text-[var(--hr-muted)]">Mva og frakt kommer i tillegg og bekreftes sammen med leveringsdato.</p>
              {serverError && (
                <Alert className="mt-4 border-[var(--hr-red)]/30 bg-[var(--hr-red-bg)] text-[var(--hr-red)]" data-testid="checkout-error">
                  <AlertTitle className="text-[13.5px]">Kunne ikke sende bestillingen</AlertTitle>
                  <AlertDescription className="text-[13px]">{serverError}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" disabled={submitting || !lines.length} className="mt-4 h-12 w-full rounded-[12px] bg-[var(--site-orange)] text-[15px] font-semibold text-[var(--site-ink)] shadow-none hover:bg-[var(--site-orange-2)] disabled:opacity-60" data-testid="checkout-submit-button">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Sender …
                  </>
                ) : (
                  <>
                    Send bestilling <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>
              <p className="mt-3 text-center text-[12px] text-[var(--hr-muted-2)]">Du forplikter deg ikke før vi har bekreftet bestillingen.</p>
            </div>
          </aside>
        </form>
      </Container>
    </div>
  );
}
